import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { Shield, Lock, Eye, Database, Cookie, Bell } from 'lucide-react';

function PrivacyPolicy() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const styles = `
        .privacy-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%);
            padding: 2rem 1rem;
        }

        .privacy-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 24px;
            padding: 2.5rem;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(6, 182, 212, 0.15);
        }

        .privacy-title {
            font-size: 2.5rem;
            font-weight: 800;
            background: linear-gradient(90deg, #6366f1, #06b6d4);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
        }

        .last-updated {
            color: #666;
            font-size: 0.9rem;
            margin-bottom: 2rem;
        }

        .section-title {
            color: #0e4aa5;
            font-weight: 700;
            font-size: 1.3rem;
            margin: 1.5rem 0 1rem 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .section-icon {
            width: 32px;
            height: 32px;
            background: linear-gradient(135deg, #6366f1, #06b6d4);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }

        .privacy-text {
            color: #444;
            line-height: 1.8;
            font-size: 1rem;
            margin-bottom: 1rem;
            text-align: left;
            font-weight: bold;
        }

        .privacy-list {
            list-style: none;
            padding-left: 0;
            margin-left: 0;
            text-align: left;
        }

        .privacy-list li {
            color: #555;
            line-height: 1.8;
            margin-bottom: 0.5rem;
            position: relative;
            padding-left: 1.8rem;
            text-align: left;
        }

        .privacy-list li::before {
            content: "•";
            color: #06b6d4;
            font-weight: bold;
            position: absolute;
            left: 0.4rem;
        }

        @media (max-width: 768px) {
            .privacy-card {
                padding: 1.5rem;
            }
            .privacy-title {
                font-size: 2rem;
            }
            .section-title {
                font-size: 1.2rem;
            }
        }
    `;

    return (
        <div className="privacy-container">
            <style>{styles}</style>
            <div className="container" style={{ maxWidth: 900 }}>
                {/* Back Button */}
                <button
                    className="btn btn-outline-primary d-flex align-items-center mb-4"
                    onClick={() => navigate('/')}
                    style={{
                        borderRadius: '12px',
                        padding: '10px 20px',
                        borderWidth: '2px',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0d53bd';
                        e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#0d53bd';
                    }}
                >
                    <FaCircleArrowLeft className="me-2" size={18} />
                    Back to Home
                </button>

                <div className="privacy-card">
                    <h1 className="privacy-title">Privacy Policy</h1>
                    <p className="last-updated">Last Updated: April 20, 2026</p>

                    <p className="privacy-text">
                        At InsightDraft AI, we take your privacy seriously. This Privacy Policy explains how we collect, 
                        use, disclose, and safeguard your information when you use our AI-powered business analysis platform.
                    </p>

                    {/* Section 1 */}
                    <div className="section-title">
                        <span className="section-icon"><Database size={18} /></span>
                        Information We Collect
                    </div>
                    <p className="privacy-text">
                        We collect information that you voluntarily provide to us when you:
                    </p>
                    <ul className="privacy-list">
                        <li>Fill out business analysis forms (business name, industry, revenue data, etc.)</li>
                        <li>Create an account or register on our platform</li>
                        <li>Contact us via email or support channels</li>
                        <li>Subscribe to our newsletter or marketing communications</li>
                    </ul>

                    {/* Section 2 */}
                    <div className="section-title">
                        <span className="section-icon"><Eye size={18} /></span>
                        How We Use Your Information
                    </div>
                    <p className="privacy-text">
                        We use the information we collect to:
                    </p>
                    <ul className="privacy-list">
                        <li>Generate AI-powered business analysis and insights</li>
                        <li>Improve our AI models and service quality</li>
                        <li>Communicate with you about your analysis results</li>
                        <li>Send you updates, newsletters, and promotional content (with your consent)</li>
                        <li>Monitor and analyze usage patterns to enhance user experience</li>
                    </ul>

                    {/* Section 3 */}
                    <div className="section-title">
                        <span className="section-icon"><Shield size={18} /></span>
                        Data Security
                    </div>
                    <p className="privacy-text">
                        We implement industry-standard security measures to protect your data:
                    </p>
                    <ul className="privacy-list">
                        <li>End-to-end encryption for all data transmissions</li>
                        <li>Secure cloud storage with regular backups</li>
                        <li>Access controls and authentication protocols</li>
                        <li>Regular security audits and vulnerability assessments</li>
                        <li>Anonymization of data used for AI model training</li>
                    </ul>

                    {/* Section 4 */}
                    <div className="section-title">
                        <span className="section-icon"><Cookie size={18} /></span>
                        Cookies and Tracking
                    </div>
                    <p className="privacy-text">
                        We use cookies and similar tracking technologies to:
                    </p>
                    <ul className="privacy-list">
                        <li>Remember your preferences and settings</li>
                        <li>Understand how you interact with our platform</li>
                        <li>Improve site performance and user experience</li>
                        <li>Provide personalized content and recommendations</li>
                    </ul>
                    <p className="privacy-text">
                        You can control cookie settings through your browser preferences.
                    </p>

                    {/* Section 5 */}
                    <div className="section-title">
                        <span className="section-icon"><Lock size={18} /></span>
                        Data Sharing and Disclosure
                    </div>
                    <p className="privacy-text">
                        We do not sell, trade, or rent your personal information to third parties. 
                        We may share information in the following circumstances:
                    </p>
                    <ul className="privacy-list">
                        <li>With trusted service providers who assist in operating our platform</li>
                        <li>To comply with legal obligations or protect our rights</li>
                        <li>In connection with a business transfer or merger</li>
                        <li>With your explicit consent</li>
                    </ul>

                    {/* Section 6 */}
                    <div className="section-title">
                        <span className="section-icon"><Bell size={18} /></span>
                        Your Rights
                    </div>
                    <p className="privacy-text">
                        You have the right to:
                    </p>
                    <ul className="privacy-list">
                        <li>Access and review your personal data</li>
                        <li>Request correction of inaccurate information</li>
                        <li>Request deletion of your data</li>
                        <li>Opt-out of marketing communications</li>
                        <li>Withdraw consent at any time</li>
                    </ul>

                    {/* Section 7 */}
                    <div className="section-title">
                        <span className="section-icon"><Shield size={18} /></span>
                        Contact Us
                    </div>
                    <p className="privacy-text">
                        If you have questions or concerns about this Privacy Policy, please contact us at:
                    </p>
                    <p className="privacy-text">
                        <strong>Email:</strong> c84289798@gmail.com<br />
                        <strong>Location:</strong> Ahmedabad, India
                    </p>
                </div>
            </div>
        </div>
    );
}

export default PrivacyPolicy;